import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, CalendarDaysIcon, MapPinIcon, SparklesIcon, Trash2Icon, TriangleAlertIcon, TruckIcon } from 'lucide-react';
import { categoryLabels, notices as seedNotices, type Notice, type NoticeCategory } from '../../data/avis';
const icons: Record<NoticeCategory, typeof BellIcon> = {
  urgent: TriangleAlertIcon,
  service: TruckIcon,
  loisirs: CalendarDaysIcon,
  personnalise: SparklesIcon,
  municipal: BellIcon
};
const filters = ['All', 'Important', 'Unread', 'Services'] as const;
export type NoticeFilter = (typeof filters)[number];
interface NoticeFeedProps {
  filter: NoticeFilter;
  onFilterChange: (filter: NoticeFilter) => void;
}
export function NoticeFeed({
  filter,
  onFilterChange
}: NoticeFeedProps) {
  const [items, setItems] = useState<Notice[]>(seedNotices);
  const [removed, setRemoved] = useState<Notice | null>(null);
  const visible = useMemo(() => {
    if (filter === 'Important') return items.filter(item => item.category === 'urgent');
    if (filter === 'Unread') return items.filter(item => item.unread);
    if (filter === 'Services') return items.filter(item => item.category === 'service');
    return items;
  }, [filter, items]);
  const toggleRead = (id: string) => setItems(current => current.map(item => item.id === id ? {
    ...item,
    unread: !item.unread
  } : item));
  const remove = (notice: Notice) => {
    setItems(current => current.filter(item => item.id !== notice.id));
    setRemoved(notice);
    window.setTimeout(() => setRemoved(value => value?.id === notice.id ? null : value), 5000);
  };
  const undo = () => {
    if (!removed) return;
    setItems(current => [removed, ...current]);
    setRemoved(null);
  };
  return <section aria-labelledby="feed-title" className="mt-14" data-id="element-349" data-mp="8emhgedjkoz7">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-350" data-mp="8d334tbg4o02">
        <h2 id="feed-title" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-351" data-mp="desde6qugxn3">
          Your notifications
        </h2>
        <ul className="flex flex-wrap gap-2" data-id="element-352" data-mp="2g2h74nisfpwi">
          {filters.map(option => <li key={option} data-id="element-353" data-mp="2b2mhenar16d1">
              <button type="button" aria-pressed={filter === option} onClick={() => onFilterChange(option)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${filter === option ? 'bg-ville-indigo text-white' : 'border border-slate-200 bg-white text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-354" data-mp="2b3ebtusm0y1q">
                {option}
              </button>
            </li>)}
        </ul>
      </div>

      {visible.length === 0 ? <p className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500" data-id="element-355" data-mp="1idfk1rn16mtd">
          No notices in this view.
        </p> : <ul className="mt-5 space-y-3" data-id="element-356" data-mp="3cqwxumxmw5b7">
          {visible.map(notice => {
        const Icon = icons[notice.category];
        const urgent = notice.category === 'urgent';
        return <li key={notice.id} className={`group relative rounded-2xl border bg-white px-5 py-4 transition-colors duration-150 ease-ville ${urgent ? 'border-slate-200 border-l-4 border-l-red-600' : 'border-slate-200'}`} data-id="element-357" data-mp="3k8bxly0bjybh">
                <div className="flex items-start gap-4" data-id="element-358" data-mp="2tew29n9fxda">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${urgent ? 'bg-red-50 text-red-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} aria-hidden="true" data-id="element-359" data-mp="2swqgmepk6sp">
                    <Icon className="h-5 w-5" data-id="element-360" data-mp="36ax0e73tqm8y" />
                  </span>

                  <div className="min-w-0 flex-1" data-id="element-361" data-mp="3b8gglck3193j">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-362" data-mp="3bbujqn0klmc1">
                      {notice.unread && <span className="h-2 w-2 rounded-full bg-ville-indigo" aria-label="Unread" data-id="element-363" data-mp="3g6i2e58w7mip" />}
                      <h3 className={`text-[15px] leading-snug ${notice.unread ? 'font-extrabold text-ville-navy' : 'font-semibold text-slate-600'}`} data-id="element-364" data-mp="qlddlbdayoyv">
                        {notice.title}
                      </h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${urgent ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-365" data-mp="p23lantreh39">
                        {categoryLabels[notice.category]}
                      </span>
                    </div>
                    <p className={`mt-1 text-sm leading-relaxed ${notice.unread ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-366" data-mp="1c2jh7ucsihzf">
                      {notice.description}
                    </p>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" data-id="element-367" data-mp="17z50f1h11xkx">
                      <span data-id="element-368" data-mp="1gt9l135kics0">{notice.when}</span>
                      {notice.context && <span className="flex items-center gap-1" data-id="element-369" data-mp="1gu1ikjiq6kkx">
                          <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-370" data-mp="1vrlktio8m1ne" />
                          {notice.context}
                        </span>}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1" data-id="element-371" data-mp="2aeairg2uc6ro">
                    <button type="button" onClick={() => toggleRead(notice.id)} className="rounded-full px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-372" data-mp="2eekmem35ul3d">
                      {notice.unread ? 'Mark as read' : 'Mark as unread'}
                    </button>
                    <button type="button" onClick={() => remove(notice)} aria-label={`Remove the notice: ${notice.title}`} className="grid h-9 w-9 place-items-center rounded-full text-slate-400 opacity-0 transition-[opacity,color] duration-150 ease-ville hover:text-ville-navy focus-visible:opacity-100 group-hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-373" data-mp="1vg8aiskv6x43">
                      <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-374" data-mp="1cofj32lg1jyh" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}

      <AnimatePresence data-id="element-375" data-mp="otlvo7yziq5v">
        {removed && <motion.div role="status" initial={{
        y: 12,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 8,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-full bg-ville-navy px-5 py-3 text-sm text-white shadow-xl" data-id="element-376" data-mp="oskm67xmabq9">
            Notification removed
            <button type="button" onClick={undo} className="font-bold text-ville-lime underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-377" data-mp="12labal8fzwrl">
              Undo
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}